import {AbstractControl, ValidationErrors} from '@angular/forms';

/** Example shown in the EMV input hint (illustrative CRC suffix). */
export const pixEmvHintExample =
  '00020126350014BR.GOV.BCB.PIX0114+5521999999999520400005303986540510.005802BR5914Nome do Recebedor6009Nova Iguacu62070503***6304ABCD';

const pixGui = 'BR.GOV.BCB.PIX';
const merchantAccountTagMin = 26;
const merchantAccountTagMax = 51;

type TlvField = {id: string; length: number; value: string};

/** Decoded merchant data from a valid PIX Copia e Cola (EMV) payload. */
export type PixEmvDecoded = {
  merchantName: string;
  merchantCity: string;
  /** `null` when tag 54 is absent (open-amount PIX). */
  amount: number | null;
  /** PIX key from merchant account template sub-ID `01`, when present. */
  pixKey: string | null;
};

/** CRC-16/CCITT-FALSE (poly 0x1021, init 0xFFFF) over payload including trailing `6304`. */
export function pixEmvCrc16(payloadWith6304: string): string {
  let crc = 0xffff;
  for (let i = 0; i < payloadWith6304.length; i++) {
    crc ^= payloadWith6304.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = ((crc << 1) ^ 0x1021) & 0xffff;
      } else {
        crc = (crc << 1) & 0xffff;
      }
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}

function parseTlv(
  payload: string,
  offset = 0,
  end?: number,
): {fields: TlvField[]; nextOffset: number} {
  const limit = end ?? payload.length;
  const fields: TlvField[] = [];
  let i = offset;

  while (i < limit) {
    if (i + 4 > limit) {
      return {fields, nextOffset: -1};
    }
    const id = payload.substring(i, i + 2);
    const length = Number.parseInt(payload.substring(i + 2, i + 4), 10);
    if (!Number.isFinite(length) || length < 0 || i + 4 + length > limit) {
      return {fields, nextOffset: -1};
    }
    const value = payload.substring(i + 4, i + 4 + length);
    fields.push({id, length, value});
    i += 4 + length;
  }

  return {fields, nextOffset: i};
}

function nestedHasPixGui(value: string): boolean {
  const nested = parseTlv(value);
  if (nested.nextOffset !== value.length) {
    return false;
  }
  return nested.fields.some(
    f => f.id === '00' && f.value.toUpperCase() === pixGui,
  );
}

function hasPixMerchantAccount(fields: TlvField[]): boolean {
  return fields.some(f => {
    const idNum = Number.parseInt(f.id, 10);
    return (
      idNum >= merchantAccountTagMin &&
      idNum <= merchantAccountTagMax &&
      nestedHasPixGui(f.value)
    );
  });
}

function extractPixKey(fields: TlvField[]): string | null {
  for (const f of fields) {
    const idNum = Number.parseInt(f.id, 10);
    if (idNum < merchantAccountTagMin || idNum > merchantAccountTagMax) {
      continue;
    }
    const nested = parseTlv(f.value);
    if (nested.nextOffset !== f.value.length || !nestedHasPixGui(f.value)) {
      continue;
    }
    const keyField = nested.fields.find(n => n.id === '01');
    const key = keyField?.value.trim();
    if (key) {
      return key;
    }
  }
  return null;
}

function parseEmvAmount(value: string): number | null {
  const raw = value.trim();
  if (!raw) {
    return null;
  }
  const normalized = raw.replace(',', '.');
  const amount = Number.parseFloat(normalized);
  if (!Number.isFinite(amount) || amount < 0) {
    return null;
  }
  return amount;
}

function decodePixEmvPayload(payload: string): PixEmvDecoded | null {
  if (!payload || payload.length < 20) {
    return null;
  }

  if (!/^[\x20-\x7E]+$/.test(payload)) {
    return null;
  }

  const crcTagIndex = payload.lastIndexOf('6304');
  if (crcTagIndex < 0 || payload.length < crcTagIndex + 8) {
    return null;
  }

  const crcGiven = payload.slice(-4).toUpperCase();
  if (!/^[0-9A-F]{4}$/.test(crcGiven)) {
    return null;
  }

  const payloadForCrc = payload.slice(0, -4);
  if (!payloadForCrc.endsWith('6304')) {
    return null;
  }

  if (pixEmvCrc16(payloadForCrc) !== crcGiven) {
    return null;
  }

  const body = payload.slice(0, crcTagIndex);
  const parsed = parseTlv(body);
  if (parsed.nextOffset !== body.length) {
    return null;
  }

  const format = parsed.fields.find(f => f.id === '00');
  if (!format || format.value !== '01') {
    return null;
  }

  if (!hasPixMerchantAccount(parsed.fields)) {
    return null;
  }

  const currency = parsed.fields.find(f => f.id === '53');
  if (currency && currency.value !== '986') {
    return null;
  }

  const country = parsed.fields.find(f => f.id === '58');
  if (country && country.value !== 'BR') {
    return null;
  }

  const merchantName = parsed.fields.find(f => f.id === '59');
  if (!merchantName || !merchantName.value.trim()) {
    return null;
  }

  const merchantCity = parsed.fields.find(f => f.id === '60');
  if (!merchantCity || !merchantCity.value.trim()) {
    return null;
  }

  const amountField = parsed.fields.find(f => f.id === '54');
  let amount: number | null = null;
  if (amountField) {
    const parsedAmount = parseEmvAmount(amountField.value);
    if (parsedAmount === null) {
      return null;
    }
    amount = parsedAmount;
  }

  return {
    merchantName: merchantName.value.trim(),
    merchantCity: merchantCity.value.trim(),
    amount,
    pixKey: extractPixKey(parsed.fields),
  };
}

/** Validates a PIX Copia e Cola (EMV BR Code) payload. */
export function isValidPixEmv(raw: string): boolean {
  return decodePixEmvPayload(raw.trim()) !== null;
}

/** Parses a valid PIX Copia e Cola payload; returns `null` when invalid. */
export function parsePixEmv(raw: string): PixEmvDecoded | null {
  return decodePixEmvPayload(raw.trim());
}

export function validatePixEmv(
  control: AbstractControl,
): ValidationErrors | null {
  const raw = String(control.value ?? '').trim();
  if (!raw) {
    return null;
  }
  return isValidPixEmv(raw) ? null : {invalidPixEmv: true};
}
